import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import type { ReleaseCustomFieldAnswer, ReleaseDetail } from "../../shared/types/api";
import {
  Card,
  CardBody,
  CardHeader,
  Field,
  InlineAlert,
  Input,
  LoadingState,
  Select,
  Textarea,
} from "../../shared/ui";
import { useReleaseMutation } from "./useReleaseMutation";

/**
 * The organisation's own questions about this release (§58-§61).
 *
 * <p>Saved one field at a time, on blur, rather than behind a single Save button. These are
 * usually filled in across a week as the answers become known — a CAB reference on Tuesday, a
 * classification on Thursday — and a form that only commits as a whole means the Tuesday answer
 * is lost by whoever closes the tab.
 *
 * <p>Required fields are marked and are enforced: an unanswered one fails the readiness gate by
 * name. A field labelled required that nothing checked would be the same defect as a gate that
 * cannot fail.
 */
export function ReleaseFieldsCard({
  release,
  onChanged,
}: {
  release: ReleaseDetail;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const { run, error, saving } = useReleaseMutation(release.id);

  const fields = useQuery({
    queryKey: ["release-fields", release.id],
    queryFn: () =>
      api.get<ReleaseCustomFieldAnswer[]>(`/api/v1/releases/${release.id}/fields`),
  });

  useEffect(() => {
    if (!fields.data) {
      return;
    }
    setDrafts(
      Object.fromEntries(fields.data.map((field) => [field.fieldId, field.value ?? ""])),
    );
  }, [fields.data]);

  const rows = fields.data ?? [];
  if (rows.length === 0) {
    // Nothing has been defined for this project. An empty card on every release would be noise.
    return null;
  }

  const save = (field: ReleaseCustomFieldAnswer) => {
    const value = drafts[field.fieldId] ?? "";
    if (value === (field.value ?? "")) {
      return;
    }
    void run(
      () =>
        api.put(`/api/v1/releases/${release.id}/fields/${field.fieldId}`, { value }),
      onChanged,
    );
  };

  const set = (fieldId: string, value: string) =>
    setDrafts((current) => ({ ...current, [fieldId]: value }));

  const missing = rows.filter(
    (field) => field.required && (drafts[field.fieldId] ?? "").trim() === "",
  );

  return (
    <Card>
      <CardHeader
        title="Additional details"
        subtitle={
          missing.length === 0
            ? "Everything this organisation asks of a release."
            : `${missing.length} still to answer`
        }
      />
      <CardBody className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {fields.isPending ? (
          <LoadingState label="Loading fields…" />
        ) : (
          rows.map((field) => {
            const value = drafts[field.fieldId] ?? "";
            const readOnly = !can("RELEASE_EDIT");
            return (
              <Field
                key={field.fieldId}
                label={field.label}
                required={field.required}
                hint={field.helpText ?? undefined}
              >
                {field.fieldType === "SELECT" ? (
                  <Select
                    value={value}
                    disabled={readOnly || saving}
                    onChange={(event) => {
                      set(field.fieldId, event.target.value);
                      void run(
                        () =>
                          api.put(`/api/v1/releases/${release.id}/fields/${field.fieldId}`, {
                            value: event.target.value,
                          }),
                        onChanged,
                      );
                    }}
                  >
                    <option value="">Not answered</option>
                    {field.options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                ) : field.fieldType === "BOOLEAN" ? (
                  <Select
                    value={value}
                    disabled={readOnly || saving}
                    onChange={(event) => {
                      set(field.fieldId, event.target.value);
                      void run(
                        () =>
                          api.put(`/api/v1/releases/${release.id}/fields/${field.fieldId}`, {
                            value: event.target.value,
                          }),
                        onChanged,
                      );
                    }}
                  >
                    <option value="">Not answered</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </Select>
                ) : field.fieldType === "TEXT_AREA" ? (
                  <Textarea
                    rows={3}
                    value={value}
                    readOnly={readOnly}
                    onChange={(event) => set(field.fieldId, event.target.value)}
                    onBlur={() => save(field)}
                  />
                ) : (
                  <Input
                    type={
                      field.fieldType === "NUMBER"
                        ? "number"
                        : field.fieldType === "DATE"
                          ? "date"
                          : field.fieldType === "URL"
                            ? "url"
                            : "text"
                    }
                    value={value}
                    readOnly={readOnly}
                    onChange={(event) => set(field.fieldId, event.target.value)}
                    onBlur={() => save(field)}
                  />
                )}
              </Field>
            );
          })
        )}
      </CardBody>
    </Card>
  );
}
